import React, { useState, Fragment } from "react";
import { allUsers } from "../../../data/mockData";
import AdminDataTable from "../components/AdminDataTable";
import Button from "../../../components/ui/Button";
import CreatorDetailModal from "../components/CreatorDetailModal";
import { MoreVertical, Eye, Trash2 } from "lucide-react";
import { Menu, Transition } from "@headlessui/react";

const CreatorCRMPage = () => {
  // Initialize creators with stable mock stats so they match between table and modal
  const [creators, setCreators] = useState(() => {
    return allUsers.filter(u => u.role === "Creator").map(user => ({
      ...user,
      followers: user.stats?.followers || Math.floor(Math.random() * 50000 + 1000),
      postsCount: Math.floor(Math.random() * 150) + 20,
      subscribers: Math.floor(Math.random() * 500) + 50,
      productsCount: Math.floor(Math.random() * 20) + 2,
      revenue: Math.floor(Math.random() * 100000 + 10000),
      activeAds: Math.floor(Math.random() * 5),
      category: user.category || ["Music", "Fitness", "Education", "Art", "Tech"][Math.floor(Math.random() * 5)]
    }));
  });
  
  const [selectedCreator, setSelectedCreator] = useState(null);

  const creatorColumns = [
    {
      header: "Creator",
      key: "name",
      width: "220px",
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold border border-purple-200">
            {row.name.charAt(0)}
          </div>
          <div>
            <p className="font-medium text-slate-800">{row.name}</p>
            <p className="text-xs text-slate-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: "Category",
      key: "category",
      width: "120px",
      render: (row) => (
        <span className="text-sm px-2 py-1 bg-purple-50 text-purple-700 rounded-lg border border-purple-100">
          {row.category}
        </span>
      ),
    },
    {
      header: "Performance",
      key: "performance",
      width: "160px",
      render: (row) => (
        <div className="space-y-1">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Followers:</span>
            <span className="font-semibold text-blue-600">{(row.followers / 1000).toFixed(1)}k</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Subs:</span>
            <span className="font-semibold text-purple-600">{row.subscribers}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Content / Ads",
      key: "content",
      width: "160px",
      render: (row) => (
        <div className="space-y-1">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Posts:</span>
            <span className="font-semibold text-slate-700">{row.postsCount}</span>
          </div>
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-500">Active Ads:</span>
            <span className="font-semibold text-orange-600">{row.activeAds}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Revenue (ETB)",
      key: "revenue",
      width: "150px",
      render: (row) => (
        <div>
          <p className="font-bold text-green-700">{row.revenue.toLocaleString()}</p>
          <p className="text-xs text-slate-500 font-medium">Lifetime Gross</p>
        </div>
      ),
    },
    {
      header: "Status",
      key: "status",
      width: "100px",
      render: (row) => (
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${
            row.status === "Active"
              ? "bg-green-100 text-green-700"
              : "bg-slate-100 text-slate-700"
          }`}
        >
          {row.status}
        </span>
      ),
    },
    {
      header: "Actions",
      key: "actions",
      width: "100px",
      render: (row) => (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-slate-400 hover:text-purple-600 hover:bg-purple-50"
            onClick={() => setSelectedCreator(row)}
            title="View Profile"
          >
            <Eye size={16} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="w-8 h-8 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
            onClick={() => {/* Suspend logic */}}
            title="Suspend Creator"
          >
            <Trash2 size={16} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Creator Management</h1>
          <p className="text-slate-500 mt-1">
            Comprehensive view of all creator activity, content, and revenue across the platform.
          </p>
        </div>
      </div>

      <AdminDataTable 
        title="All Creators" 
        columns={creatorColumns.map(c => ({...c, sortable: true}))} 
        data={creators}
        searchableFields={["name", "email", "category"]}
        filterableFields={[
          { key: "status", label: "Status", options: ["Active", "Suspended"] },
          // Note: Since 'category' in mockData is sometimes random in render, filtering might be tricky unless strictly defined in data object.
          // For now we'll stick to status.
        ]} 
      />
      
      {selectedCreator && (
        <CreatorDetailModal 
          creator={selectedCreator} 
          onClose={() => setSelectedCreator(null)} 
        />
      )}
    </div>
  );
};

export default CreatorCRMPage;
